import { FC, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Heading, HeadingLevel, HeadingTag } from '@enbw/ui-react'
import { Contract } from '../types'
import { InitialState } from './InitialState'
import { ResultsTable } from './ResultsTable'
import { NoResults } from './NoResults'
import styles from './styles.module.scss'
import { PageSizeSelector, PaginationComponent } from '@/components/Pagination'
import { CardLayout } from '@/components/CardLayout'

type SearchResultsProps = {
  readonly hasSearched: boolean
  readonly contracts: Contract[]
  readonly searchQuery: string
  readonly isPending: boolean
  readonly totalCount?: number
  /**
   * Current page number (0-indexed for backend)
   */
  readonly currentPage: number
  readonly pageSize: number
  /**
   * Called when user navigates to a different page
   * @param page - The page number (0-indexed for backend)
   */
  readonly onPageChange: (page: number) => void
  readonly onPageSizeChange: (pageSize: number) => void
}

export const SearchResults: FC<SearchResultsProps> = ({
  hasSearched,
  contracts,
  searchQuery,
  isPending,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange
}) => {
  const { t } = useTranslation()
  const scrollTargetRef = useRef<HTMLDivElement>(null)
  const displayCount = totalCount ?? contracts.length

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 1

  const shouldShowPagination = totalCount !== undefined && totalCount > pageSize

  const handlePageChange = (page1Indexed: number): void => {
    // Convert from 1-indexed (UI) to 0-indexed (backend)
    onPageChange(page1Indexed - 1)
    // Scroll to the top of the results section
    requestAnimationFrame(() => {
      scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  if (!hasSearched) {
    return (
      <CardLayout padded>
        <InitialState />
      </CardLayout>
    )
  }

  return (
    <>
      <div ref={scrollTargetRef} />
      <CardLayout
        title={
          <div className={styles.cardTitleBar}>
            <Heading level={HeadingLevel.H2} tag={HeadingTag.H2}>
              {t('contracts.results', { count: displayCount })}
            </Heading>
            {shouldShowPagination && <PageSizeSelector itemsPerPage={pageSize} onPageSizeChange={onPageSizeChange} />}
          </div>
        }
      >
        <div className={styles.resultsContainer}>
          {contracts.length > 0 ? (
            <>
              <ResultsTable contracts={contracts} />
              {shouldShowPagination && (
                <div className={styles.paginationContainer}>
                  <PaginationComponent
                    currentPage={currentPage + 1}
                    totalPages={totalPages}
                    itemsPerPage={pageSize}
                    totalItems={totalCount ?? contracts.length}
                    onPageChange={handlePageChange}
                    onPageSizeChange={onPageSizeChange}
                  />
                </div>
              )}
            </>
          ) : (
            !isPending && <NoResults searchQuery={searchQuery} />
          )}
        </div>
      </CardLayout>
    </>
  )
}
